import React, { FC } from 'react'
import classNames from 'classnames'
import { ImageCardProps } from './types.d'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import VideoAnimation from '@toasttab/shared/src/components/atoms/VideoAnimation/VideoAnimation'
import VidyardVideoPlayer from '@toasttab/shared/src/components/molecules/VidyardVideo/VidyardVideoPlayer'
import VidyardPlayButton from '@toasttab/shared/src/components/molecules/VidyardPlayButton/VidyardPlayButton'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { LottieProps } from '@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer.d'
const LottiePlayer: FC<LottieProps> =
  typeof window !== 'undefined' || process.env.NODE_ENV === 'test'
    ? require('@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer')
        .default
    : () => null

const ImageCard = ({
  badge,
  bgColor,
  cardBorderColor,
  ctaLabel,
  ctaLink,
  dataTestId = 'ImageCard-1',
  elevation,
  featuredImage,
  headingBadge,
  image,
  imageRound,
  video,
  linkIcons,
  loopAnimation,
  padding,
  sectionDescription,
  sectionHeading,
  slideIsActive,
  textAlignment,
  fourCardLayout = false
}: ImageCardProps) => {
  const renderContentImage = () => {
    let imageRoundClass = imageRound ? 'rounded-lg' : 'rounded-t-lg'
    const imgClasses = classNames(
      imageRoundClass,
      'max-h-[220px] w-auto mx-auto',
      {
        'lg:h-[220px]': !fourCardLayout,
        'lg:h-[161px] lg:max-h-[161px]': fourCardLayout
      }
    )
    switch (image[0].extension) {
      case 'json':
        return (
          <LottiePlayer
            classes={imageRoundClass}
            corners={imageRound}
            direction={1}
            hover
            lottieFile={image[0].url}
            previewImage={featuredImage}
          />
        )
      case 'mp4':
      case 'webm':
        return (
          <div className={`${imageRoundClass} w-full relative overflow-hidden`}>
            <VideoAnimation
              animation={image[0]}
              autoPlay
              classes={`${imageRoundClass} w-full h-full object-cover`}
              corners
              loop={loopAnimation}
              previewImage={featuredImage}
            />
          </div>
        )
      default:
        return (
          <img
            alt=''
            className={imgClasses}
            src={`${
              image[0].optimizedImages
                ? image[0].optimizedImages.srcAvif
                : image[0].url
            }`}
          />
        )
    }
  }

  const renderImageVideo = () => {
    return (
      <div className='col-span-4 md:col-span-6 relative [&>div:nth-child(1)]:z-10 [&>div:nth-child(2)]:z-0'>
        {renderVideo(video.vidyardId, tabIndex, video.videoButtonClasses)}
        {renderVideoImage(video.image)}
      </div>
    )
  }

  const renderVideo = (
    vidyardId: string,
    tabIndex: number,
    videoButtonClasses: string
  ) => {
    if (!vidyardId) return
    const videoButtonCss = classNames(
      'bg-white inline-flex items-center justify-center h-[56px] py-5 px-7 m-auto',
      videoButtonClasses
    )
    return (
      <div
        className='flex absolute top-0 bottom-0 left-0 right-0'
        data-testid='TestimonialCard-Video'
      >
        <VidyardPlayButton
          onClick={() => {
            if (typeof window !== 'undefined') {
              const vidyardEmbed = require('@vidyard/embed-code')
              // @ts-ignore - Typescript doesn't like setting new properties on window
              let players = vidyardEmbed.api.getPlayersByUUID(vidyardId)
              if (players && players.length > 0) {
                players[0].showLightbox()
              }
            }
          }}
          variation='secondary'
          classes={videoButtonCss}
          tabIndex={tabIndex}
        />
        <div className='hidden'>
          <VidyardVideoPlayer vidyardId={vidyardId} />
        </div>
      </div>
    )
  }

  const renderVideoImage = (image: any) => {
    if (!image) {
      return
    }
    const imgClasses = classNames(
      image.classes,
      'w-full h-full max-h-[220px] w-auto mx-auto',
      {
        'rounded-lg lg:rounded-md': imageRound,
        'rounded-t-lg lg:rounded-t-md': !imageRound,
        'lg:h-[220px]': !fourCardLayout,
        'lg:h-[161px] lg:max-h-[161px]': fourCardLayout
      }
    )
    return <Image {...image} classes={imgClasses} />
  }

  const wrapperClasses = classNames(
    'rounded-lg',
    Card.backgroundColor(bgColor),
    Card.borderColor(cardBorderColor),
    Card.elevation(elevation),
    {
      border: Card.borderColor(cardBorderColor) !== '',
      'hover:shadow-3xl':
        ctaLink && ctaLabel && Card.borderColor(cardBorderColor) !== ''
    }
  )
  let tabIndex = 0
  if (slideIsActive) {
    tabIndex = slideIsActive ? 0 : -1
  }

  return (
    <div
      className={wrapperClasses}
      data-testid={dataTestId}
      tabIndex={tabIndex}
    >
      {image && renderContentImage()}
      {video && renderImageVideo()}
      <div
        className={`${Text.alignment(textAlignment)} ${Card.cardPadding(
          padding
        )}`}
        data-testid={`${dataTestId}-content`}
      >
        {badge && headingBadge && (
          <Badge
            text={badge}
            badgeColor='bg-sky-0'
            classes='mb-4'
            uppercase={true}
          />
        )}
        <Typography
          as='h4'
          variation='h4'
          classes='text-default mb-4 !font-normal'
        >
          {sectionHeading}
        </Typography>
        <Typography as='p' variation='default' classes='text-gray-100 mb-4'>
          {sectionDescription}
        </Typography>
        {badge && !headingBadge && (
          <div className='mb-3'>
            <Badge text={badge} badgeColor='bg-sky-0' uppercase={true} />
          </div>
        )}
        {ctaLink && ctaLabel && (
          <TextLink
            classes={linkIcons}
            href={ctaLink}
            icon={linkIcons ? linkIcons : 'none'}
            text={ctaLabel}
            tabIndex={tabIndex}
          />
        )}
      </div>
    </div>
  )
}

export default ImageCard
